/**
 * Mouth Gesture Setup
 * Multi-stage wizard for configuring mouth gesture control
 * 
 * Uses unified scanning system for consistent behavior with external switches
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { getScoped, setScoped, removeScoped } from '@/lib/userScopedStorage';
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MainLayout } from "@/components/layout/MainLayout";
import { cn } from "@/lib/utils";
import { Check, ChevronRight, ChevronLeft, Camera, Smile, CircleDot, Heart } from "lucide-react";
import { getUserSettings, saveUserSettings } from "@/lib/userSettings";
import { getAvailableActionsForInputCount, getActionLabel, ScanningAction, isAutomaticScanning } from "@/lib/scanningSystem";
import { useTranslation } from "@/hooks/useTranslation";

// Setup stages
type SetupStage = "config" | "calibrate_neutral" | "calibrate_gesture" | "test" | "complete";

// Gesture types
type GestureType = "open_mouth" | "smile" | "kiss";

// Gesture action types - use unified scanning system types
type GestureAction = ScanningAction;

interface GestureConfig {
  id: GestureType;
  label: string;
  icon: React.ElementType;
  description: string;
}

// GESTURES will be translated dynamically in the component using t()

// MediaPipe face landmarks for mouth
const MOUTH_LANDMARKS = {
  LEFT_CORNER: 61,
  RIGHT_CORNER: 291,
  UPPER_LIP: 13,
  LOWER_LIP: 14,
  UPPER_OUTER: 0,
  LOWER_OUTER: 17,
};

interface CalibrationData {
  neutralWidth: number;
  neutralHeight: number;
  gestures: {
    [key in GestureType]?: {
      width: number;
      height: number;
      threshold: number;
    };
  };
}

export default function MouthGestureSetup() {
  const navigate = useNavigate();
  const { t, dir } = useTranslation();
  
  // Define GESTURES with translations
  const GESTURES: GestureConfig[] = [
    { id: "open_mouth", label: t("openMouth"), icon: CircleDot, description: t("openMouthDesc") },
    { id: "smile", label: t("smile"), icon: Smile, description: t("smileDesc") },
    { id: "kiss", label: t("kiss"), icon: Heart, description: t("kissDesc") },
  ];
  
  const [stage, setStage] = useState<SetupStage>("config");
  
  // Configuration state
  const [selectedGestures, setSelectedGestures] = useState<GestureType[]>([]);
  const [gestureActions, setGestureActions] = useState<{ [key in GestureType]?: GestureAction }>({});
  const [scanMode, setScanMode] = useState<"manual" | "automatic">("automatic");
  const [scanType, setScanType] = useState<"cell_by_cell" | "row_and_column">("cell_by_cell");
  const [scanSpeed, setScanSpeed] = useState(1000);
  
  // Check if existing calibration exists
  const [hasExistingCalibration, setHasExistingCalibration] = useState(false);
  
  // Calibration state
  const [currentGestureIndex, setCurrentGestureIndex] = useState(0);
  const [calibrationData, setCalibrationData] = useState<CalibrationData>({
    neutralWidth: 0,
    neutralHeight: 0,
    gestures: {},
  });
  const [isCapturing, setIsCapturing] = useState(false);
  const [captureCountdown, setCaptureCountdown] = useState(0);
  
  // Tracking state
  const [isTracking, setIsTracking] = useState(false);
  const [currentMouthState, setCurrentMouthState] = useState<"neutral" | GestureType>("neutral");
  const [testResults, setTestResults] = useState<{ [key in GestureType]?: boolean }>({});
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const faceLandmarkerRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const lastMouthDataRef = useRef({ width: 0, height: 0 });
  const stageRef = useRef<SetupStage>(stage);
  const calibrationDataRef = useRef<CalibrationData>(calibrationData);
  const selectedGesturesRef = useRef<GestureType[]>(selectedGestures);

  // Keep refs in sync with state
  useEffect(() => {
    stageRef.current = stage;
  }, [stage]);
  
  useEffect(() => {
    calibrationDataRef.current = calibrationData;
  }, [calibrationData]);
  
  useEffect(() => {
    selectedGesturesRef.current = selectedGestures;
  }, [selectedGestures]);

  // State for initialization status
  const [initError, setInitError] = useState<string | null>(null);

  // Check for existing calibration on mount
  useEffect(() => {
    const savedCalibration = getScoped("ayuni_mouth_calibration");
    if (savedCalibration) {
      try {
        const parsed = JSON.parse(savedCalibration);
        if (parsed.neutralWidth && parsed.neutralHeight) {
          setHasExistingCalibration(true);
          console.log('[MouthGesture] Found existing calibration data');
        }
      } catch (e) {
        console.log('[MouthGesture] Invalid saved calibration');
      }
    }
    
    // Also load saved settings
    const settings = getUserSettings();
    if (settings.mouthGestures && settings.mouthGestures.length > 0) {
      setSelectedGestures(settings.mouthGestures as GestureType[]);
    }
    if (settings.mouthGestureActions) {
      setGestureActions(settings.mouthGestureActions as { [key in GestureType]?: GestureAction });
    }
    if (settings.scanMode) {
      setScanType(settings.scanMode as "cell_by_cell" | "row_and_column");
    }
    if (settings.scanSpeed) {
      setScanSpeed(settings.scanSpeed * 1000);
    }
  }, []);

  // Initialize MediaPipe
  const initializeMediaPipe = useCallback(async () => {
    try {
      console.log('[MouthGesture] Loading MediaPipe...');
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        filesetResolver,
        getFaceLandmarkerOptions('GPU', true)
      );
      
      console.log('[MouthGesture] MediaPipe loaded successfully');
      return true;
    } catch (err) {
      console.log('[MouthGesture] GPU failed, trying CPU...', err);
      // Fallback to CPU
      try {
        const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
        
        const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
        
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          filesetResolver,
          getFaceLandmarkerOptions('CPU', true)
        );
        
        console.log('[MouthGesture] MediaPipe loaded with CPU fallback');
        return true;
      } catch (err2) {
        console.error('[MouthGesture] MediaPipe failed:', err2);
        setInitError(t("faceModelError"));
        return false;
      }
    }
  }, [t]);

  // Setup webcam
  const setupWebcam = useCallback(async () => {
    try {
      console.log('[MouthGesture] Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: "user" }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        console.log('[MouthGesture] Camera started successfully');
        return true;
      }
      setInitError(t("videoError"));
      return false;
    } catch (err) {
      console.error('[MouthGesture] Camera error:', err);
      setInitError(t("cameraError"));
      return false;
    }
  }, [t]);

  // Extract mouth measurements from landmarks
  const extractMouthData = useCallback((landmarks: any[]) => {
    const leftCorner = landmarks[MOUTH_LANDMARKS.LEFT_CORNER];
    const rightCorner = landmarks[MOUTH_LANDMARKS.RIGHT_CORNER];
    const upperLip = landmarks[MOUTH_LANDMARKS.UPPER_LIP];
    const lowerLip = landmarks[MOUTH_LANDMARKS.LOWER_LIP];
    
    const width = Math.abs(rightCorner.x - leftCorner.x);
    const height = Math.abs(lowerLip.y - upperLip.y);
    
    return { width, height };
  }, []);

  // Detect current gesture based on calibration (using refs for current values)
  const detectGestureFromRefs = useCallback((mouthData: { width: number; height: number }): "neutral" | GestureType => {
    const calibData = calibrationDataRef.current;
    const gestures = selectedGesturesRef.current;
    
    if (!calibData.neutralWidth || !calibData.neutralHeight) {
      console.log('[MouthGesture] No calibration data');
      return "neutral";
    }
    
    const widthRatio = mouthData.width / calibData.neutralWidth;
    const heightRatio = mouthData.height / calibData.neutralHeight;
    
    // Debug logging (throttled)
    if (Math.random() < 0.1) {
      console.log('[MouthGesture] widthRatio:', widthRatio.toFixed(2), 'heightRatio:', heightRatio.toFixed(2));
    }
    
    // Check each calibrated gesture with more lenient thresholds
    for (const gestureType of gestures) {
      const gestureCalib = calibData.gestures[gestureType];
      if (!gestureCalib) continue;
      
      // Use calibrated gesture data for comparison
      const calibWidthRatio = gestureCalib.width / calibData.neutralWidth;
      const calibHeightRatio = gestureCalib.height / calibData.neutralHeight;
      
      // More lenient threshold detection - use 50% of the calibrated difference
      if (gestureType === "open_mouth") {
        const threshold = 1 + (calibHeightRatio - 1) * 0.5;
        if (heightRatio > Math.max(threshold, 1.2)) {
          console.log('[MouthGesture] Detected: open_mouth');
          return "open_mouth";
        }
      }
      if (gestureType === "smile") {
        const widthThreshold = 1 + (calibWidthRatio - 1) * 0.5;
        if (widthRatio > Math.max(widthThreshold, 1.05) && heightRatio < 1.6) {
          console.log('[MouthGesture] Detected: smile');
          return "smile";
        }
      }
      if (gestureType === "kiss") {
        const widthThreshold = 1 - (1 - calibWidthRatio) * 0.5;
        if (widthRatio < Math.min(widthThreshold, 0.95)) {
          console.log('[MouthGesture] Detected: kiss');
          return "kiss";
        }
      }
    }
    
    return "neutral";
  }, []);

  // State for face detection feedback
  const [faceDetected, setFaceDetected] = useState(false);

  // Draw face mesh as dot grid on black background (like head tracking)
  const drawFaceMesh = useCallback((canvas: HTMLCanvasElement, landmarks: any[]) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    // Clear with black background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "black";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Primary color for avatar (teal/primary)
    const primaryColor = "#22c55e"; // Green when tracking
    
    // Face outline points
    const faceOutlineIndices = [
      10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 
      397, 365, 379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 
      172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109
    ];
    
    // Eye points
    const eyeIndices = [
      // Left eye
      33, 7, 163, 144, 145, 153, 154, 155, 133, 173, 157, 158, 159, 160, 161, 246,
      // Right eye
      362, 382, 381, 380, 374, 373, 390, 249, 263, 466, 388, 387, 386, 385, 384, 398,
    ];
    
    // Nose points
    const noseIndices = [168, 6, 197, 195, 5, 4, 1, 19, 94, 2, 98, 327];
    
    // Mouth points (emphasized)
    const mouthIndices = [
      61, 146, 91, 181, 84, 17, 314, 405, 321, 375, 291, 308, 324, 318, 402, 317, 14, 87, 178, 88, 95,
      78, 191, 80, 81, 82, 13, 312, 311, 310, 415, 308,
    ];
    
    // Eyebrow points
    const eyebrowIndices = [
      70, 63, 105, 66, 107, 55, 65, 52, 53, 46,
      300, 293, 334, 296, 336, 285, 295, 282, 283, 276,
    ];
    
    // Draw face outline (gray)
    ctx.fillStyle = "#666666";
    faceOutlineIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw eyes (gray)
    eyeIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw eyebrows (gray)
    eyebrowIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw nose (gray)
    noseIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Draw mouth points with primary/accent color (emphasized - larger dots)
    ctx.fillStyle = primaryColor;
    mouthIndices.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Key face points (larger)
    const keyPoints = [1, 33, 263, 61, 291, 168, 10, 152];
    ctx.fillStyle = primaryColor;
    keyPoints.forEach(idx => {
      if (idx < landmarks.length) {
        const point = landmarks[idx];
        ctx.beginPath();
        ctx.arc(point.x * canvas.width, point.y * canvas.height, 3, 0, Math.PI * 2);
        ctx.fill();
      }
    });
  }, []);

  // Tracking loop
  const runTrackingLoop = useCallback(() => {
    if (!videoRef.current || !faceLandmarkerRef.current) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      return;
    }
    
    const video = videoRef.current;
    
    if (video.readyState >= 2) {
      const results = faceLandmarkerRef.current.detectForVideo(video, performance.now());
      
      if (results.faceLandmarks && results.faceLandmarks.length > 0) {
        const landmarks = results.faceLandmarks[0];
        const mouthData = extractMouthData(landmarks);
        lastMouthDataRef.current = mouthData;
        setFaceDetected(true);
        
        // Draw face mesh on canvas
        if (canvasRef.current) {
          drawFaceMesh(canvasRef.current, landmarks);
        }
        
        // Detect gesture during test stage (use ref to get current stage)
        if (stageRef.current === "test") {
          const detectedGesture = detectGestureFromRefs(mouthData);
          setCurrentMouthState(detectedGesture);
          
          // Mark gesture as successful if detected
          if (detectedGesture !== "neutral" && selectedGesturesRef.current.includes(detectedGesture)) {
            setTestResults(prev => ({ ...prev, [detectedGesture]: true }));
          }
        }
      } else {
        setFaceDetected(false);
        // Draw empty black canvas when no face detected
        if (canvasRef.current) {
          const ctx = canvasRef.current.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.fillStyle = "black";
            ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      }
    }
    
    animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
  }, [extractMouthData, detectGestureFromRefs, drawFaceMesh]);

  // Start tracking
  const startTracking = useCallback(async () => {
    const mpReady = await initializeMediaPipe();
    if (!mpReady) return;
    
    const webcamReady = await setupWebcam();
    if (!webcamReady) return;
    
    setIsTracking(true);
    runTrackingLoop();
  }, [initializeMediaPipe, setupWebcam, runTrackingLoop]);

  // Stop tracking
  const stopTracking = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
    }
    
    setIsTracking(false);
  }, []);

  // Capture calibration data
  const captureCalibration = useCallback(() => {
    setIsCapturing(true);
    setCaptureCountdown(3);
    
    const countdownInterval = setInterval(() => {
      setCaptureCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          
          // Capture current mouth data
          const mouthData = lastMouthDataRef.current;
          
          if (stage === "calibrate_neutral") {
            setCalibrationData(prev => ({
              ...prev,
              neutralWidth: mouthData.width,
              neutralHeight: mouthData.height,
            }));
            setIsCapturing(false);
            
            // Move to first gesture calibration
            if (selectedGestures.length > 0) {
              setCurrentGestureIndex(0);
              setStage("calibrate_gesture");
            } else {
              setStage("test");
            }
          } else if (stage === "calibrate_gesture") {
            const currentGesture = selectedGestures[currentGestureIndex];
            setCalibrationData(prev => ({
              ...prev,
              gestures: {
                ...prev.gestures,
                [currentGesture]: {
                  width: mouthData.width,
                  height: mouthData.height,
                  threshold: 1.2,
                },
              },
            }));
            setIsCapturing(false);
            
            // Move to next gesture or test
            if (currentGestureIndex < selectedGestures.length - 1) {
              setCurrentGestureIndex(prev => prev + 1);
            } else {
              setStage("test");
            }
          }
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stage, selectedGestures, currentGestureIndex]);

  // Toggle gesture selection
  const toggleGesture = (gesture: GestureType) => {
    setSelectedGestures(prev => {
      let newGestures: GestureType[];
      if (prev.includes(gesture)) {
        // Remove gesture and its action
        setGestureActions(actions => {
          const newActions = { ...actions };
          delete newActions[gesture];
          return newActions;
        });
        newGestures = prev.filter(g => g !== gesture);
      } else {
        if (prev.length >= 3) return prev;
        newGestures = [...prev, gesture];
      }
      
      // Auto-set scan mode based on gesture count using unified scanning system
      // 1-2 gestures: automatic, 3+ gestures: manual
      const shouldBeAutomatic = isAutomaticScanning(newGestures.length);
      setScanMode(shouldBeAutomatic ? "automatic" : "manual");
      
      return newGestures;
    });
  };

  // Set action for a gesture
  const setGestureAction = (gesture: GestureType, action: GestureAction) => {
    setGestureActions(prev => ({ ...prev, [gesture]: action }));
  };

  // Get available actions based on number of selected gestures
  // Uses unified scanning system for consistent behavior with external switches
  const getAvailableActions = (): GestureAction[] => {
    return getAvailableActionsForInputCount(selectedGestures.length) as GestureAction[];
  };

  // Initialize tracking when moving past config stage
  useEffect(() => {
    if (stage !== "config" && !isTracking) {
      startTracking();
    }
    return () => {
      if (stage === "complete") {
        stopTracking();
      }
    };
  }, [stage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopTracking();
  }, [stopTracking]);

  // Navigation handlers
  const handleBack = () => {
    if (stage === "config") {
      navigate("/control");
    } else if (stage === "calibrate_neutral") {
      stopTracking();
      setStage("config");
    } else if (stage === "calibrate_gesture") {
      if (currentGestureIndex > 0) {
        setCurrentGestureIndex(prev => prev - 1);
      } else {
        setStage("calibrate_neutral");
      }
    } else if (stage === "test") {
      setStage("calibrate_gesture");
      setCurrentGestureIndex(selectedGestures.length - 1);
    } else if (stage === "complete") {
      setStage("test");
    }
  };

  const handleContinue = () => {
    if (stage === "config") {
      if (selectedGestures.length === 0) return;
      // Check all gestures have actions assigned
      const allHaveActions = selectedGestures.every(g => gestureActions[g]);
      if (!allHaveActions) return;
      setStage("calibrate_neutral");
    } else if (stage === "test") {
      // Save settings
      const settings = getUserSettings();
      saveUserSettings({
        ...settings,
        controlMethod: "mouth_gestures",
        scanControlMode: scanMode === "automatic" ? "automatic" : "gesture_controlled",
        scanMode: scanType,
        scanSpeed: scanSpeed / 1000,
        mouthGestures: selectedGestures,
        mouthGestureActions: gestureActions,
      });
      
      // Convert calibration data to format expected by MouthGestureEngine
      // Engine expects: neutralWidth, neutralHeight, smileThreshold, openThreshold, puckerThreshold
      const engineCalibration = {
        neutralWidth: calibrationData.neutralWidth,
        neutralHeight: calibrationData.neutralHeight,
        smileThreshold: calibrationData.gestures.smile
          ? calibrationData.gestures.smile.width / calibrationData.neutralWidth
          : 1.2,
        openThreshold: calibrationData.gestures.open_mouth
          ? calibrationData.gestures.open_mouth.height / calibrationData.neutralHeight
          : 2.0,
        puckerThreshold: calibrationData.gestures.kiss
          ? calibrationData.gestures.kiss.width / calibrationData.neutralWidth
          : 0.8,
      };
      
      // Store calibration data in localStorage
      setScoped("ayuni_mouth_calibration", JSON.stringify(engineCalibration));
      
      sessionStorage.setItem("controlMethod", "mouth_gestures");
      window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
        detail: { method: "mouth_gestures" } 
      }));
      
      setStage("complete");
    } else if (stage === "complete") {
      stopTracking();
      navigate("/");
    }
  };

  // Skip calibration and use existing data
  const handleSkipCalibration = () => {
    // Save settings without recalibrating
    const settings = getUserSettings();
    saveUserSettings({
      ...settings,
      controlMethod: "mouth_gestures",
      scanControlMode: scanMode === "automatic" ? "automatic" : "gesture_controlled",
      scanMode: scanType,
      scanSpeed: scanSpeed / 1000,
      mouthGestures: selectedGestures,
      mouthGestureActions: gestureActions,
    });
    
    sessionStorage.setItem("controlMethod", "mouth_gestures");
    window.dispatchEvent(new CustomEvent("controlMethodChanged", { 
      detail: { method: "mouth_gestures" } 
    }));
    
    navigate("/");
  };

  // Render configuration stage
  const renderConfigStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6 p-4">
      <h2 className="text-2xl font-bold text-foreground">{t("mouthGestureSetupTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("selectUpTo3Gestures")}
      </p>
      
      {/* Gesture selection */}
      <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
        {GESTURES.map(gesture => {
          const Icon = gesture.icon;
          const isSelected = selectedGestures.includes(gesture.id);
          
          return (
            <motion.button
              key={gesture.id}
              whileTap={{ scale: 0.95 }}
              onClick={() => toggleGesture(gesture.id)}
              className={cn(
                "flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all",
                isSelected
                  ? "border-accent bg-accent/10"
                  : "border-border bg-card hover:border-primary/50"
              )}
            >
              <Icon className={cn("w-10 h-10", isSelected ? "text-accent" : "text-primary")} />
              <span className="font-medium text-foreground">{gesture.label}</span>
              <span className="text-xs text-muted-foreground text-center">{gesture.description}</span>
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="w-5 h-5 text-accent" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
      
      {/* Action mapping for selected gestures */}
      {selectedGestures.length > 0 && (
        <div className="w-full max-w-lg space-y-4 mt-4">
          <h3 className="font-semibold text-foreground">{t("actionMapping")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("selectActionForGesture")}
          </p>
          
          <div className="space-y-3">
            {selectedGestures.map(gestureId => {
              const gesture = GESTURES.find(g => g.id === gestureId);
              if (!gesture) return null;
              const Icon = gesture.icon;
              const availableActions = getAvailableActions();
              
              return (
                <div key={gestureId} className="p-3 rounded-xl bg-card border border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-accent" />
                    <span className="font-medium text-foreground">{gesture.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableActions.map(actionId => {
                      const actionLabelKey = getActionLabel(actionId as ScanningAction);
                      const actionLabel = t(actionLabelKey as any) || actionLabelKey;
                      const isAssigned = gestureActions[gestureId] === actionId;
                      const isUsedByOther = Object.entries(gestureActions).some(
                        ([g, a]) => g !== gestureId && a === actionId
                      );
                      
                      return (
                        <button
                          key={actionId}
                          onClick={() => setGestureAction(gestureId, actionId)}
                          disabled={isUsedByOther}
                          className={cn(
                            "px-3 py-1.5 rounded-lg text-sm transition-all",
                            isAssigned
                              ? "bg-accent text-accent-foreground"
                              : isUsedByOther
                                ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed"
                                : "bg-muted hover:bg-muted/80 text-foreground"
                          )}
                        >
                          {actionLabel}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Scanning settings */}
      <div className="w-full max-w-lg space-y-4 mt-4">
        <h3 className="font-semibold text-foreground">{t("scanSettingsTitle")}</h3>
        
        {/* Scan mode - display only, auto-set based on gesture count */}
        <div className="p-3 rounded-xl bg-card border border-border">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">{t("scanModeLabel")}</span>
            <span className="font-medium text-primary">
              {selectedGestures.length >= 3 ? t("manualScanning") : t("automaticScan")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {selectedGestures.length >= 3 
              ? t("gesture3ManualDesc")
              : selectedGestures.length === 2
                ? t("gesture2AutoDesc")
                : t("gesture1AutoDesc")
            }
          </p>
        </div>
        
        {/* Scan type */}
        <div className="flex gap-2">
          <button
            onClick={() => setScanType("cell_by_cell")}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl border-2 transition-all",
              scanType === "cell_by_cell"
                ? "border-accent bg-accent/10 text-foreground"
                : "border-border bg-card text-muted-foreground"
            )}
          >
            {t("scanCellsMode")}
          </button>
          <button
            onClick={() => setScanType("row_and_column")}
            className={cn(
              "flex-1 py-3 px-4 rounded-xl border-2 transition-all",
              scanType === "row_and_column"
                ? "border-accent bg-accent/10 text-foreground"
                : "border-border bg-card text-muted-foreground"
            )}
          >
            {t("rowsAndColumns")}
          </button>
        </div>
        
        {/* Scan speed - Only show for automatic scanning (less than 3 gestures) */}
        {selectedGestures.length < 3 && (
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">
              {t("scanSpeedSeconds").replace("{0}", String(scanSpeed / 1000))}
            </label>
            <input
              type="range"
              min={500}
              max={3000}
              step={100}
              value={scanSpeed}
              onChange={(e) => setScanSpeed(Number(e.target.value))}
              className="w-full accent-primary"
            />
          </div>
        )}
        
        {/* Skip calibration option if existing calibration exists */}
        {hasExistingCalibration && selectedGestures.length > 0 && selectedGestures.every(g => gestureActions[g]) && (
          <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
            <p className="text-sm text-green-600 dark:text-green-400 mb-3 text-center">
              {t("usePreviousCalibrationOption")}
            </p>
            <div className="flex gap-3 justify-center">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleSkipCalibration}
                className="px-6 py-2 rounded-xl bg-green-500 text-white font-medium"
              >
                {t("usePreviousBtn")}
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setHasExistingCalibration(false)}
                className="px-6 py-2 rounded-xl border border-border bg-card text-foreground font-medium"
              >
                {t("recalibrateBtn")}
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render neutral calibration stage
  const renderNeutralCalibration = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <h2 className="text-2xl font-bold text-foreground">{t("neutralCalibrationTitle")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("keepFaceRelaxed")}
      </p>
      
      {/* Video preview */}
      <div className="relative w-80 h-60 bg-black rounded-2xl overflow-hidden border-4 border-border">
        <canvas ref={canvasRef} width={320} height={240} className="w-full h-full" />
        
        {!faceDetected && isTracking && (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-muted-foreground">{t("searchingFace")}</p>
          </div>
        )}
        
        {isCapturing && captureCountdown > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <span className="text-6xl font-bold text-white">{captureCountdown}</span>
          </div>
        )}
      </div>
      
      {/* Face detection indicator */}
      <div className={cn(
        "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
        initError 
          ? "bg-red-500/20 text-red-500"
          : faceDetected 
            ? "bg-green-500/20 text-green-500" 
            : "bg-orange-500/20 text-orange-500"
      )}>
        {initError ? initError : (isTracking ? (faceDetected ? t("faceDetected") : t("faceNotDetected")) : t("initializingTracking"))}
      </div>
      
      <motion.button
        whileTap={{ scale: 0.95 }}
        onClick={captureCalibration}
        disabled={isCapturing || !faceDetected}
        className={cn(
          "flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-lg transition-all",
          isCapturing || !faceDetected
            ? "bg-muted text-muted-foreground"
            : "bg-primary text-primary-foreground hover:opacity-90"
        )}
      >
        <Camera className="w-6 h-6" />
        {isCapturing ? t("capturing") : t("capture")}
      </motion.button>
    </div>
  );

  // Render gesture calibration stage
  const renderGestureCalibration = () => {
    const currentGesture = GESTURES.find(g => g.id === selectedGestures[currentGestureIndex]);
    if (!currentGesture) return null;
    
    const Icon = currentGesture.icon;
    
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-6">
        <h2 className="text-2xl font-bold text-foreground">
          {t("gestureCalibrationTitle").replace("{0}", currentGesture.label)}
        </h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t("performGestureDesc").replace("{0}", currentGesture.description)}
        </p>
        
        {/* Gesture icon */}
        <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center">
          <Icon className="w-12 h-12 text-accent" />
        </div>
        
        {/* Video preview */}
        <div className="relative w-80 h-60 bg-black rounded-2xl overflow-hidden border-4 border-border">
          <canvas ref={canvasRef} width={320} height={240} className="w-full h-full" />
          
          {!faceDetected && isTracking && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-muted-foreground">{t("searchingFace")}</p>
            </div>
          )}
          
          {isCapturing && captureCountdown > 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <span className="text-6xl font-bold text-white">{captureCountdown}</span>
            </div>
          )}
        </div>
        
        {/* Progress indicator */}
        <div className="flex gap-2">
          {selectedGestures.map((_, idx) => (
            <div
              key={idx}
              className={cn(
                "w-3 h-3 rounded-full",
                idx === currentGestureIndex ? "bg-accent" : idx < currentGestureIndex ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>
        
        <motion.button
          whileTap={{ scale: 0.95 }}
          onClick={captureCalibration}
          disabled={isCapturing || !faceDetected}
          className={cn(
            "flex items-center gap-2 px-8 py-4 rounded-2xl font-bold text-lg transition-all",
            isCapturing || !faceDetected
              ? "bg-muted text-muted-foreground"
              : "bg-primary text-primary-foreground hover:opacity-90"
          )}
        >
          <Camera className="w-6 h-6" />
          {isCapturing ? t("capturing") : t("capture")}
        </motion.button>
      </div>
    );
  };

  // Render test stage
  const renderTestStage = () => {
    const allTested = selectedGestures.every(g => testResults[g]);
    
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-6">
        <h2 className="text-2xl font-bold text-foreground">{t("gestureTestTitle")}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {t("testGesturesDesc")}
        </p>
        
        {/* Video preview */}
        <div className="relative w-80 h-60 bg-black rounded-2xl overflow-hidden border-4 border-border">
          <canvas ref={canvasRef} width={320} height={240} className="w-full h-full" />
          
          {!faceDetected && isTracking && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-muted-foreground">{t("searchingFace")}</p>
            </div>
          )}
          
          {/* Current state indicator */}
          {faceDetected && (
            <div className={cn(
              "absolute bottom-2 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-medium",
              currentMouthState === "neutral"
                ? "bg-muted text-muted-foreground"
                : "bg-accent text-accent-foreground"
            )}>
              {currentMouthState === "neutral" ? t("neutralState") : GESTURES.find(g => g.id === currentMouthState)?.label}
            </div>
          )}
        </div>
        
        {/* Gesture test results */}
        <div className="flex gap-4">
          {selectedGestures.map(gestureId => {
            const gesture = GESTURES.find(g => g.id === gestureId);
            if (!gesture) return null;
            
            const Icon = gesture.icon;
            const isCompleted = testResults[gestureId];
            
            return (
              <div
                key={gestureId}
                className={cn(
                  "flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all",
                  isCompleted
                    ? "border-accent bg-accent/10"
                    : "border-border bg-card"
                )}
              >
                <Icon className={cn("w-8 h-8", isCompleted ? "text-accent" : "text-muted-foreground")} />
                <span className="text-sm font-medium">{gesture.label}</span>
                {isCompleted && <Check className="w-5 h-5 text-accent" />}
              </div>
            );
          })}
        </div>
        
        {allTested && (
          <p className="text-accent font-medium">{t("allGesturesDetected")}</p>
        )}
      </div>
    );
  };

  // Render complete stage
  const renderCompleteStage = () => (
    <div className="flex flex-col items-center justify-center flex-1 gap-6">
      <div className="w-24 h-24 rounded-full bg-accent/20 flex items-center justify-center">
        <Check className="w-14 h-14 text-accent" />
      </div>
      
      <h2 className="text-2xl font-bold text-foreground">{t("setupComplete")}</h2>
      <p className="text-muted-foreground text-center max-w-md">
        {t("gesturesCalibrated")}
      </p>
      
      <div className="flex flex-wrap justify-center gap-2">
        {selectedGestures.map(gestureId => {
          const gesture = GESTURES.find(g => g.id === gestureId);
          if (!gesture) return null;
          
          return (
            <span
              key={gestureId}
              className="px-4 py-2 rounded-full bg-accent/20 text-accent font-medium"
            >
              {gesture.label}
            </span>
          );
        })}
      </div>
    </div>
  );

  // Get stage title
  const getStageTitle = () => {
    switch (stage) {
      case "config": return t("configStage");
      case "calibrate_neutral": return t("calibrateStage");
      case "calibrate_gesture": return t("calibrateGestureStage");
      case "test": return t("testStage");
      case "complete": return t("completeStage");
    }
  };

  // Can continue to next stage
  const canContinue = () => {
    if (stage === "config") {
      // Must have at least one gesture and all gestures must have actions assigned
      return selectedGestures.length > 0 && selectedGestures.every(g => gestureActions[g]);
    }
    if (stage === "test") return selectedGestures.every(g => testResults[g]);
    return true;
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background overflow-hidden" dir="rtl">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <h1 className="text-xl font-bold text-foreground text-center">
            {t("mouthGesturesTitle")} - {getStageTitle()}
          </h1>
        </div>

        {/* Hidden video element - must persist across all stages for MediaPipe */}
        <video ref={videoRef} className="hidden" autoPlay playsInline muted />

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={stage}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="flex-1 flex flex-col overflow-auto"
          >
            {stage === "config" && renderConfigStage()}
            {stage === "calibrate_neutral" && renderNeutralCalibration()}
            {stage === "calibrate_gesture" && renderGestureCalibration()}
            {stage === "test" && renderTestStage()}
            {stage === "complete" && renderCompleteStage()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="p-4 border-t border-border flex justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-card border border-border text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
            {t("back")}
          </button>
          
          {(stage === "config" || stage === "test" || stage === "complete") && (
            <button
              onClick={handleContinue}
              disabled={!canContinue()}
              className={cn(
                "flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all",
                canContinue()
                  ? "bg-primary text-primary-foreground hover:opacity-90"
                  : "bg-muted text-muted-foreground cursor-not-allowed"
              )}
            >
              {stage === "complete" ? t("finish") : t("continue")}
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
